/*
  Administrator Functions -> Payments Received.

  Date range dropdown (dangrossman/daterangepicker) drives a grouped bar chart
  of credit card ('cc') vs check ('ck') payments per day, plus summary stats.
*/
app.controller('paymentsReceivedController', function ($rootScope, $scope, safeApply, systemService) {

  const RANGE_INPUT = '#prDateRange'
  const CANVAS_ID = 'prPaymentsChart'
  const BATCH_CANVAS_ID = 'prBatchChart'

  const TYPE_META = {
    cc: { name: 'Credit Card', colour: '#2b9cd3', border: '#1f7fb0' },
    ck: { name: 'Check', colour: '#12a150', border: '#0b7d3e' },
  }

  let picker = null
  let chart = null
  let batchChart = null
  // "<year> Tax Season" presets, built from the invoiced years on load.
  let taxSeasons = []

  /*
    user_info arrives asynchronously: crmController sets $rootScope.user_info to
    null on load and only populates it after verifyUserCredentials resolves.
    Loading on controller init therefore sent null credentials and the server
    (correctly) answered "Not authorized." - so the first load is deferred until
    user_info actually appears.
  */
  let initialLoadDone = false

  $rootScope.$watch('user_info', function () {
    $scope.user_info = $rootScope.user_info
    $scope.is_admin = !!$scope.user_info && $scope.user_info.access_level == 1

    if (!initialLoadDone && $scope.user_info && $scope.user_info.id) {
      initialLoadDone = true
      startWithDefaultRange()
      // Snapshot stats - independent of the date range, so loaded once.
      loadAppealStats()
    }
  }, true)

  $scope.dateRange = {
    label: 'This Month',
    start: moment().startOf('month'),
    end: moment().endOf('month'),
    startText: '',
    endText: '',
    days: 0,
  }

  // Appeal / invoicing stats. Not date-range driven - it is a snapshot of all
  // batched appeals, so it loads once rather than on every range change.
  $scope.appeals = {
    loaded: false,
    error: null,
    totals: {},
    reductions: { market: {}, appraised: {} },
    uncertifiedCount: 0,
    by_year: [],
    by_county: [],
    county_other: [],
    county_other_appeals: 0,
    invoicedPct: 0,
    // Residential / commercial breakdown, rendered as its own row of cards.
    divisions: [],
  }

  $scope.payments = {
    loading: false,
    loaded: false,
    error: null,
    hasData: false,
    metric: 'amount',   // 'amount' | 'count'
    daily: [],
    cards: [],
    excluded: [],
    combined: { payments: 0, total: 0, avg_amount: 0, active_days: 0 },
  }

  function applyRange(start, end, label) {
    $scope.dateRange.start = start
    $scope.dateRange.end = end
    $scope.dateRange.label = label || 'Custom Range'
    $scope.dateRange.startText = start.format('MM/DD/YYYY')
    $scope.dateRange.endText = end.format('MM/DD/YYYY')
    $scope.dateRange.days = end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1
    safeApply($scope)
  }

  init()
  function init() {
    if (typeof moment === 'undefined') {
      console.log('paymentsReceived-> moment not loaded')
      return
    }
    applyRange($scope.dateRange.start, $scope.dateRange.end, $scope.dateRange.label)
    setTimeout(mountRangePicker, 60)
    // The data load is triggered by the user_info watcher above, once
    // credentials are available.
  }

  function mountRangePicker() {
    const $input = $(RANGE_INPUT)
    if (!$input.length) return

    if (typeof $.fn.daterangepicker === 'undefined') {
      console.log('paymentsReceived-> daterangepicker not loaded')
      $input.attr('placeholder', 'Date picker unavailable')
      return
    }

    destroyRangePicker()

    $input.daterangepicker({
      startDate: $scope.dateRange.start,
      endDate: $scope.dateRange.end,
      opens: 'right',
      autoApply: false,
      showDropdowns: true,
      alwaysShowCalendars: true,
      linkedCalendars: false,
      maxDate: moment().endOf('day'),
      locale: { format: 'MM/DD/YYYY', firstDay: 0 },
      ranges: buildRanges(),
    }, function (start, end, label) {
      applyRange(start, end, label)
      loadPayments()
    })

    picker = $input.data('daterangepicker')
    $input.val($scope.dateRange.startText + ' - ' + $scope.dateRange.endText)
  }

  /*
    Picker presets. The tax seasons are listed first so they read as the primary
    choices, followed by the usual relative ranges.
  */
  function buildRanges() {
    const ranges = {}

    // "2026 Tax Season" etc., newest first.
    taxSeasons.forEach(s => {
      ranges[s.label] = [s.start.clone(), s.end.clone()]
    })

    const relative = {
      'Today': [moment(), moment()],
      'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
      'Last 7 Days': [moment().subtract(6, 'days'), moment()],
      'Last 30 Days': [moment().subtract(29, 'days'), moment()],
      'This Month': [moment().startOf('month'), moment().endOf('month')],
      'Last Month': [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      'This Quarter': [moment().startOf('quarter'), moment().endOf('quarter')],
      'Last Quarter': [
        moment().subtract(1, 'quarter').startOf('quarter'),
        moment().subtract(1, 'quarter').endOf('quarter'),
      ],
      'This Year': [moment().startOf('year'), moment().endOf('year')],
      'Last Year': [
        moment().subtract(1, 'year').startOf('year'),
        moment().subtract(1, 'year').endOf('year'),
      ],
    }

    // A season label could collide with a relative one only if a season were
    // literally named e.g. "Today", which cannot happen.
    Object.keys(relative).forEach(k => { ranges[k] = relative[k] })
    return ranges
  }

  function destroyRangePicker() {
    const existing = $(RANGE_INPUT).data('daterangepicker')
    if (existing) existing.remove()
    picker = null
  }

  /*
    Default range: earliest official_invoice_date across active appeals through
    today. Falls back to the current month if the server has no such date, or if
    the lookup fails - the screen should still load with something usable.
  */
  async function startWithDefaultRange() {
    try {
      const result = await systemService.functionCall({
        type: "getPaymentsDefaultRange",
        user_id: $scope.user_info.id,
        url_password: $scope.user_info.url_password,
      })

      if (result.data.success && result.data.data.start) {
        const data = result.data.data

        // "<year> Tax Season" presets, newest first, from the invoiced years.
        // Each runs from that year's first invoice to today when it's the
        // current season, otherwise to its own last invoice date.
        const thisYear = moment().year()
        taxSeasons = (data.seasons || []).map(s => ({
          label: s.year + ' Tax Season',
          start: moment(s.start, 'YYYY-MM-DD'),
          end: s.year === thisYear ? moment() : moment(s.end, 'YYYY-MM-DD'),
        })).filter(s => s.start.isValid() && s.end.isValid())

        const start = moment(data.start, 'YYYY-MM-DD')
        const end = moment(data.end, 'YYYY-MM-DD')
        if (start.isValid() && end.isValid()) {
          // Default to the newest season's preset so the label matches the list.
          const label = taxSeasons.length ? taxSeasons[0].label : 'Since First Invoice'
          if (taxSeasons.length) {
            applyRange(taxSeasons[0].start.clone(), taxSeasons[0].end.clone(), label)
          } else {
            applyRange(start, end, label)
          }
        }
      }

      // Remount so the season presets appear in the picker's list.
      mountRangePicker()
    } catch (error) {
      console.log('startWithDefaultRange-> error, using current month :>> ', error)
    }

    loadPayments()
  }

  // Pushes the current scope range into the picker, if it has mounted.
  function syncPickerToRange() {
    const existing = $(RANGE_INPUT).data('daterangepicker')
    if (!existing) return
    existing.setStartDate($scope.dateRange.start)
    existing.setEndDate($scope.dateRange.end)
    $(RANGE_INPUT).val($scope.dateRange.startText + ' - ' + $scope.dateRange.endText)
  }

  /*
    Appeal / invoicing snapshot for all batched appeals
    (active = 1 AND appeal_batch_id > 0). Independent of the date range.
  */
  async function loadAppealStats() {
    if (!$scope.user_info || !$scope.user_info.id) return

    try {
      const result = await systemService.functionCall({
        type: "getAppealBatchStats",
        user_id: $scope.user_info.id,
        url_password: $scope.user_info.url_password,
      })

      if (!result.data.success) throw result.data.error

      const data = result.data.data
      $scope.appeals.totals = data.totals || {}
      $scope.appeals.by_year = data.by_year || []
      $scope.appeals.reductions = data.reductions || { market: {}, appraised: {} }
      // 43 counties come back but most have nothing invoiced - charting them all
      // would be 37 empty bars. Only counties with invoiced fees are plotted;
      // the rest are summarised in a footnote.
      const counties = data.by_county || []
      $scope.appeals.by_county = counties.filter(c => c.invoiced_fees > 0)
      $scope.appeals.county_other = counties.filter(c => c.invoiced_fees <= 0)
      $scope.appeals.county_other_appeals = $scope.appeals.county_other
        .reduce((a, c) => a + c.appeals, 0)
      const appeals = $scope.appeals.totals.appeals || 0
      $scope.appeals.invoicedPct = appeals
        ? Math.round(($scope.appeals.totals.invoiced / appeals) * 100)
        : 0

      // Share of total fee value that has been invoiced. Distinct from
      // invoicedPct, which counts appeals rather than dollars.
      const feeTotal = $scope.appeals.totals.fee_total || 0
      $scope.appeals.invoicedFeePct = feeTotal
        ? Math.round(($scope.appeals.totals.invoiced_fees / feeTotal) * 100)
        : 0
      // Derived from the above rather than rounded independently, so the two
      // always add to 100% (e.g. 59.6/40.4 would otherwise show as 60/40 = 100,
      // but 60.5/39.5 would round to 61/40 = 101).
      $scope.appeals.uninvoicedFeePct = feeTotal
        ? 100 - $scope.appeals.invoicedFeePct
        : 0

      // Appeals excluded from the reduction figures because they have no
      // certified value yet.
      const withCertified = ($scope.appeals.reductions.market || {}).appeals || 0
      $scope.appeals.uncertifiedCount = Math.max(0, appeals - withCertified)

      /*
        Residential / commercial cards. Always show both even if one is empty,
        so the section layout stays stable; 'other' (BPP / untyped) is only
        shown when it actually has appeals, since it is normally a handful.
      */
      const div = data.by_division || {}
      $scope.appeals.divisions = [div.residential, div.commercial, div.other]
        .filter(d => d && (d.key !== 'other' || d.appeals > 0))
      $scope.appeals.error = null
      $scope.appeals.loaded = true
      safeApply($scope)

      setTimeout(renderBatchChart, 60)
    } catch (error) {
      console.log('loadAppealStats-> error :>> ', error)
      $scope.appeals.error = typeof error === 'string' ? error
        : (error && error.message ? error.message : 'Unable to load appeal stats.')
      $scope.appeals.loaded = true
      safeApply($scope)
    }
  }

  function destroyBatchChart() {
    if (batchChart) {
      batchChart.destroy()
      batchChart = null
    }
  }

  /*
    Horizontal stacked bars: invoiced vs not-invoiced fees per county.
    Horizontal because county names and money values read better along the
    x-axis; stacked so the outstanding amount is visible alongside the invoiced.
  */
  function renderBatchChart() {
    const canvas = document.getElementById(BATCH_CANVAS_ID)
    if (!canvas || typeof Chart === 'undefined') return
    if (!$scope.appeals.by_county.length) return

    destroyBatchChart()

    const rows = $scope.appeals.by_county
    const money = v => '$' + Number(v).toLocaleString('en-US', { maximumFractionDigits: 0 })
    const plugins = (typeof ChartDataLabels !== 'undefined') ? [ChartDataLabels] : []
    const shortMoney = v => {
      if (!v) return ''
      return v >= 1000 ? '$' + (v / 1000).toFixed(0) + 'k' : '$' + Math.round(v)
    }

    batchChart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      plugins: plugins,
      data: {
        labels: rows.map(r => r.county),
        datasets: [
          {
            label: 'Invoiced',
            data: rows.map(r => r.invoiced_fees),
            backgroundColor: '#0d9488',
            borderColor: '#0f766e',
            borderWidth: 1,
            borderRadius: 3,
            maxBarThickness: 22,
          },
          {
            label: 'Not Invoiced',
            data: rows.map(r => r.uninvoiced_fees),
            backgroundColor: '#fcd34d',
            borderColor: '#f59e0b',
            borderWidth: 1,
            borderRadius: 3,
            maxBarThickness: 22,
          },
        ],
      },
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: {
            stacked: true,
            beginAtZero: true,
            grace: '8%',
            grid: { color: '#eef2f7' },
            ticks: {
              font: { size: 10 },
              callback: v => '$' + Number(v).toLocaleString('en-US'),
            },
          },
          y: {
            stacked: true,
            grid: { display: false },
            ticks: { font: { size: 11, weight: '600' } },
          },
        },
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
            labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 10, padding: 14 },
          },
          tooltip: {
            callbacks: {
              afterBody: function (items) {
                const row = rows[items[0].dataIndex]
                return [
                  'CAD ' + row.cad_code,
                  'Appeals: ' + row.appeals.toLocaleString('en-US') +
                  ' (' + row.invoiced.toLocaleString('en-US') + ' invoiced)',
                  'Tax savings: ' + money(row.tax_savings),
                ]
              },
              label: function (ctx) {
                return ctx.dataset.label + ': ' + money(ctx.parsed.x)
              },
            },
          },
          // Only label the invoiced segment - stacked bars get crowded fast.
          datalabels: {
            display: ctx => ctx.datasetIndex === 0 && ctx.dataset.data[ctx.dataIndex] > 0,
            anchor: 'center',
            align: 'center',
            color: '#fff',
            font: { size: 9, weight: '700' },
            formatter: shortMoney,
          },
        },
      },
    })
  }

  // Loads per-day totals and stats for the selected range.
  async function loadPayments() {
    // Without credentials the server answers "Not authorized"; wait for the
    // user_info watcher instead of showing a misleading error.
    if (!$scope.user_info || !$scope.user_info.id) {
      console.log('loadPayments-> deferred, user_info not ready')
      return
    }

    $scope.payments.loading = true
    $scope.payments.error = null
    safeApply($scope)

    try {
      const result = await systemService.functionCall({
        type: "getPaymentsReceived",
        user_id: $scope.user_info ? $scope.user_info.id : null,
        url_password: $scope.user_info ? $scope.user_info.url_password : null,
        start: $scope.dateRange.start.format('YYYY-MM-DD'),
        end: $scope.dateRange.end.format('YYYY-MM-DD'),
      })

      if (!result.data.success) throw result.data.error

      const data = result.data.data
      $scope.payments.daily = data.daily || []
      $scope.payments.excluded = data.excluded || []
      buildStats(data.stats || [])
      $scope.payments.hasData = $scope.payments.daily.length > 0
      $scope.payments.loaded = true
      $scope.payments.loading = false
      safeApply($scope)

      // Canvas only exists once ng-show has revealed the card.
      setTimeout(renderChart, 60)
    } catch (error) {
      console.log('loadPayments-> error :>> ', error)
      $scope.payments.error = typeof error === 'string' ? error
        : (error && error.message ? error.message : 'Unable to load payments.')
      $scope.payments.hasData = false
      $scope.payments.loaded = true
      $scope.payments.loading = false
      destroyChart()
      safeApply($scope)
    }
  }

  // One card per type, in a fixed order, plus a combined roll-up.
  function buildStats(stats) {
    $scope.payments.cards = ['cc', 'ck'].map(key => {
      const row = stats.find(s => s.payment_type === key) || {}
      return {
        key: key,
        name: TYPE_META[key].name,
        payments: row.payments || 0,
        total: row.total || 0,
        min_amount: row.min_amount || 0,
        max_amount: row.max_amount || 0,
        avg_amount: row.avg_amount || 0,
        total_fees: row.total_fees || 0,
      }
    })

    const payments = $scope.payments.cards.reduce((a, c) => a + c.payments, 0)
    const total = $scope.payments.cards.reduce((a, c) => a + c.total, 0)
    // Distinct days that had at least one cc/ck payment.
    const days = {}
    $scope.payments.daily.forEach(d => { days[d.day] = true })

    $scope.payments.combined = {
      payments: payments,
      total: total,
      // Weighted across both types - not the mean of the two averages.
      avg_amount: payments > 0 ? total / payments : 0,
      active_days: Object.keys(days).length,
    }
  }

  $scope.setMetric = function (metric) {
    if ($scope.payments.metric === metric) return
    $scope.payments.metric = metric
    renderChart()
  }

  /*
    Grouped bar chart: one x-axis label per day in the range, two bars per day.
    Every day between start and end is emitted, so gaps read as gaps rather
    than collapsing the axis.
  */
  function buildChartData() {
    const byDay = {}
    $scope.payments.daily.forEach(d => {
      if (!byDay[d.day]) byDay[d.day] = {}
      byDay[d.day][d.payment_type] = d
    })

    const labels = []
    const series = { cc: [], ck: [] }
    const useCount = $scope.payments.metric === 'count'

    const cursor = $scope.dateRange.start.clone().startOf('day')
    const last = $scope.dateRange.end.clone().startOf('day')
    // Guard against a pathological range producing thousands of bars.
    let guard = 0
    while (cursor.isSameOrBefore(last) && guard < 1000) {
      const key = cursor.format('YYYY-MM-DD')
      labels.push(cursor.format('MM/DD'))
      const day = byDay[key] || {}
        ;['cc', 'ck'].forEach(t => {
          const row = day[t]
          series[t].push(row ? (useCount ? row.payments : row.amount) : 0)
        })
      cursor.add(1, 'day')
      guard++
    }

    return { labels: labels, series: series, useCount: useCount }
  }

  function destroyChart() {
    if (chart) {
      chart.destroy()
      chart = null
    }
  }

  function renderChart() {
    const canvas = document.getElementById(CANVAS_ID)
    if (!canvas) return
    if (typeof Chart === 'undefined') {
      console.log('paymentsReceived-> Chart.js not loaded')
      return
    }

    destroyChart()

    const built = buildChartData()
    const money = v => '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    // Compact label: $1.2k / $980 for amounts, plain integers for counts.
    const shortLabel = function (value) {
      if (!value) return ''           // hide zero-value bars' labels
      if (built.useCount) return value.toLocaleString('en-US')
      if (value >= 1000000) return '$' + (value / 1000000).toFixed(1) + 'm'
      if (value >= 1000) return '$' + (value / 1000).toFixed(1) + 'k'
      return '$' + Math.round(value)
    }

    // Opted in per chart - the plugin is unregistered globally in index.html so
    // it cannot affect the other Chart.js charts in the app.
    const plugins = (typeof ChartDataLabels !== 'undefined') ? [ChartDataLabels] : []

    chart = new Chart(canvas.getContext('2d'), {
      type: 'bar',
      plugins: plugins,
      data: {
        labels: built.labels,
        datasets: ['cc', 'ck'].map(t => ({
          label: TYPE_META[t].name,
          data: built.series[t],
          backgroundColor: TYPE_META[t].colour,
          borderColor: TYPE_META[t].border,
          borderWidth: 1,
          borderRadius: 3,
          maxBarThickness: 26,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: {
            position: 'top',
            align: 'end',
            labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 10, padding: 16 },
          },
          tooltip: {
            callbacks: {
              label: function (ctx) {
                const v = ctx.parsed.y
                return ctx.dataset.label + ': ' + (built.useCount ? v.toLocaleString('en-US') : money(v))
              },
            },
          },
          // Value above each bar. Rotated when the range is wide enough that
          // horizontal labels would collide.
          datalabels: {
            anchor: 'end',
            align: 'end',
            offset: 2,
            rotation: built.labels.length > 14 ? -90 : 0,
            clamp: true,
            color: '#5e6e82',
            font: { size: built.labels.length > 20 ? 9 : 10, weight: '600' },
            formatter: shortLabel,
            // Drop labels that cannot fit rather than overlapping them.
            display: function (ctx) {
              return ctx.dataset.data[ctx.dataIndex] ? 'auto' : false
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { autoSkip: true, maxRotation: 0, font: { size: 11 } },
          },
          y: {
            beginAtZero: true,
            // Headroom so the value labels above the tallest bar are not clipped.
            grace: built.labels.length > 14 ? '18%' : '10%',
            grid: { color: '#eef2f7' },
            ticks: {
              font: { size: 11 },
              callback: function (value) {
                return built.useCount
                  ? value
                  : '$' + Number(value).toLocaleString('en-US')
              },
            },
          },
        },
      },
    })
  }

  // Both the picker and the chart live outside Angular's lifecycle.
  $scope.$on('$destroy', function () {
    destroyRangePicker()
    destroyChart()
    destroyBatchChart()
  })

})
